/**
 * When the weekly report is on offer, and which week is currently open.
 *
 * The same split the tour and the first meeting use: "which week is open right
 * now" is a property of this page load and lives in memory, "which week has
 * already been read" is a property of the account on this device and lives in
 * Dexie meta (`ACCOUNT_META_KEYS` clears it when the phone changes hands).
 *
 * It never opens itself. The unlock is an offer — a card on Today and a push
 * that deep-links here — because a full-screen report that appears over
 * whatever you were doing is an interruption, and this one is meant to be
 * read, not dismissed.
 */

import { addDays, deviceTimeZone, localDateString, startOfWeek } from '@gramas/shared'
import { create } from 'zustand'
import { getMeta, setMeta } from '../db/dexie.js'

export const WRAPPED_SEEN_META_KEY = 'wrapped.seenWeek'

/**
 * Sunday evening, late enough that the week is actually over.
 *
 * Nine, not six. The diary week runs Monday to Sunday, so at six the Sunday
 * dinner is usually still unlogged — and opening the card is what marks the
 * week read, so an early offer means the report someone actually sits down
 * with is the one missing their own evening. Kept in step with the weekly
 * push's `WEEKLY_HOUR`, which invites people to the same page.
 */
export const WRAPPED_UNLOCK_HOUR = 21

/**
 * The week currently being offered, or null outside the window.
 *
 * Sunday from 21:00 offers the week that is ending; Monday, all day, still
 * offers it — a report that expires at midnight on the one evening people are
 * least likely to open the app is a report nobody reads. From Tuesday it is
 * archive material, reachable from Tendencijos but no longer pushed forward.
 * Wrapped recomputes from the live diary every time it opens, so a Monday
 * reader gets the finished week rather than whatever was true on Sunday.
 */
export function wrappedOnOffer(now: Date): string | null {
  const day = now.getDay()
  const localDate = localDateString(now, deviceTimeZone())
  if (day === 0 && now.getHours() >= WRAPPED_UNLOCK_HOUR) return startOfWeek(localDate)
  if (day === 1) return startOfWeek(addDays(localDate, -1))
  return null
}

export async function seenWrappedWeek(): Promise<string | null> {
  return getMeta<string | null>(WRAPPED_SEEN_META_KEY, null)
}

export async function markWrappedSeen(weekStart: string): Promise<void> {
  await setMeta(WRAPPED_SEEN_META_KEY, weekStart)
}

interface WrappedState {
  /** The Monday of the week being shown, or null when nothing is open. */
  weekStart: string | null
}

export const useWrappedState = create<WrappedState>(() => ({ weekStart: null }))

export function openWrapped(weekStart: string): void {
  useWrappedState.setState({ weekStart })
  void markWrappedSeen(weekStart)
}

export function closeWrapped(): void {
  useWrappedState.setState({ weekStart: null })
}
